import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
    ArrowLeft, Send, Plus, Trash2, MessageSquare, Loader2,
    ChevronRight, Search, Lightbulb, X, BookOpen, ExternalLink,
    Link as LinkIcon, Play, Heart, Square, BrainCircuit, Sparkles
} from 'lucide-react';
import { VoiceButton } from '../components/VoiceButton';
import { trackServer } from '../lib/analytics';

const API = '/api';

const isHebrewText = (text) => {
    if (!text) return false;
    const heb = (text.match(/[\u0590-\u05FF]/g) || []).length;
    const lat = (text.match(/[a-zA-Z]/g) || []).length;
    return heb > lat;
};

const SourceCard = ({ source, navigate }) => {
    const [bookmark, setBookmark] = useState(null);

    useEffect(() => {
        if (source.bookmark_id) {
            fetch(`${API}/bookmarks/${source.bookmark_id}`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setBookmark(data); })
                .catch((err) => { console.error('Failed to load source bookmark:', err); });
        }
    }, [source.bookmark_id]);

    const bm = bookmark || source;
    const hostname = bm.url ? (() => { try { return new URL(bm.url).hostname; } catch { return ''; } })() : '';

    return (
        <div
            className="flex gap-3 p-2.5 rounded-xl cursor-pointer transition-all hover:shadow-md bg-white/80 backdrop-blur-sm border border-border/40"
            onClick={() => navigate(`/bookmark/${source.bookmark_id}`)}
            data-testid={`source-card-${source.bookmark_id}`}
        >
            {bm.thumbnail && (
                <div className="w-16 h-12 rounded-lg overflow-hidden bg-secondary shrink-0 relative">
                    <img src={bm.thumbnail} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                    {bm.content_type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
                                <Play className="w-2.5 h-2.5 text-white ml-px" fill="white" />
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: '#333' }}>{bm.title || 'Untitled'}</p>
                {hostname && (
                    <div className="flex items-center gap-1 mt-0.5">
                        <LinkIcon className="w-2.5 h-2.5" style={{ color: '#aaa' }} />
                        <span className="text-[10px] truncate" style={{ color: '#aaa' }}>{hostname}</span>
                    </div>
                )}
            </div>
            <ExternalLink className="w-3 h-3 shrink-0 mt-1" style={{ color: '#ccc' }} />
        </div>
    );
};

const AIChatPage = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const inputRef = useRef(null);
    const textareaRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const abortControllerRef = useRef(null);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [typingText, setTypingText] = useState('');
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [showSidebar, setShowSidebar] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    // Prevent pull-to-refresh on Android TWA
    useEffect(() => {
        const container = scrollContainerRef.current;
        const html = document.documentElement;
        const prevHtml = html.style.overscrollBehavior;
        const prevBody = document.body.style.overscrollBehavior;
        html.style.overscrollBehavior = 'none';
        document.body.style.overscrollBehavior = 'none';

        let startY = 0;
        const onTouchStart = (e) => { startY = e.touches[0].pageY; };
        const onTouchMove = (e) => {
            const y = e.touches[0].pageY;
            const delta = y - startY;
            if (delta > 0 && window.scrollY <= 0) {
                const scrollableParent = container && container.contains(e.target) ? container : null;
                if (!scrollableParent || scrollableParent.scrollTop <= 0) {
                    e.preventDefault();
                }
            }
        };
        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            html.style.overscrollBehavior = prevHtml;
            document.body.style.overscrollBehavior = prevBody;
        };
    }, []);

    // Auto-focus input on mount
    useEffect(() => {
        if (!authLoading && user) {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 300);
        }
    }, [authLoading, user]);

    const scrollToBottom = useCallback((smooth = true) => {
        messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    }, []);

    useEffect(() => { scrollToBottom(); }, [messages, typingText, scrollToBottom]);

    // Load sessions
    const loadSessions = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ai-chat/sessions`, { credentials: 'include' });
            if (res.ok) setSessions(await res.json());
        } catch (err) { console.error('Load sessions error:', err); }
    }, []);

    useEffect(() => { if (user) loadSessions(); }, [user, loadSessions]);

    // Load a session
    const loadSession = useCallback(async (sessionId) => {
        try {
            const res = await fetch(`${API}/ai-chat/sessions/${sessionId}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setMessages(data.messages || []);
                setActiveSessionId(sessionId);
                setShowSidebar(false);
                setTimeout(() => scrollToBottom(false), 100);
            }
        } catch (e) { toast.error('Failed to load conversation'); }
    }, [scrollToBottom]);

    // Auto-load session from URL. Guard against a race with the
    // auto-submit → ?session=<id> swap: if we already have this
    // session mounted (activeSessionId matches), skip the reload — the
    // messages are already in state and re-fetching from the server
    // could clobber them with a partially-persisted copy.
    useEffect(() => {
        const sid = searchParams.get('session');
        if (sid && user && sid !== activeSessionId) loadSession(sid);
    }, [searchParams, user, loadSession, activeSessionId]);

    // Auto-submit a query passed in via ?q=... This is how the merged
    // search bar on the Dashboard hands off to AI: the user types,
    // presses Enter (or taps the "Ask BrainSync" chip), and we
    // navigate here with the query in the URL. Fire it once as soon as
    // auth is ready and the chat is empty. We do NOT strip ?q from the
    // URL here — that would break back-navigation from a bookmark page
    // (browser back would land on an empty /ai-chat). Instead, the
    // sendMessage flow swaps ?q=... for ?session=<id> as soon as the
    // first response arrives, so subsequent back-navigations restore
    // the full conversation via the existing session-loader effect.
    const autoSubmittedRef = useRef(false);
    useEffect(() => {
        if (autoSubmittedRef.current) return;
        const q = searchParams.get('q');
        if (!q || !user || messages.length > 0 || sending) return;
        autoSubmittedRef.current = true;
        sendMessage(q);
    }, [searchParams, user, messages.length, sending]);

    const startNewChat = () => {
        setMessages([]);
        setActiveSessionId(null);
        setInput('');
        setIsTyping(false);
        setTypingText('');
        setShowSidebar(false);
        if (abortControllerRef.current) abortControllerRef.current.abort();
        setTimeout(() => textareaRef.current?.focus(), 100);
    };

    const stopGenerating = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setSending(false);
        setIsTyping(false);
        if (typingText) {
            setMessages(prev => [...prev, { role: 'assistant', content: typingText + '\n\n*(stopped)*', sources: [], timestamp: new Date().toISOString() }]);
            setTypingText('');
        }
    };

    const sendMessage = async (overrideText) => {
        // `overrideText` lets callers (e.g. the ?q= URL-param
        // auto-submit path from the Dashboard's search bar) fire a
        // message without racing React's input state. Falls back to
        // the input field for the regular typed-message path.
        const text = (overrideText ?? input).trim();
        if (!text || sending || isTyping) return;

        // Fire `first_ai_question` — activation event for the RAG feature,
        // complementing `first_save`. Server enforces once-per-user via
        // `db.ai_chats.count == 0` guard + unique index, so we call it on
        // every ask and let the backend dedupe. This fixes the previous
        // localStorage-only guard which broke for TWA reinstalls, private
        // mode, and users who asked their first question from bookmark_chat
        // (a different component) — server-side truth now catches all paths.
        try {
            trackServer('first_ai_question', {
                // `entry_point` tells us WHERE the user asked their first
                // question from — direct chat vs the ?q= handoff from the
                // Dashboard search bar / "Ask BrainSync" chip.
                entry_point: overrideText ? 'handoff' : 'chat_input',
            });
        } catch (_) { /* analytics must never break the chat */ }

        // Haptic feedback on mobile
        if (navigator.vibrate) navigator.vibrate(10);

        const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setSending(true);
        setIsTyping(false);
        setTypingText('');

        // Reset textarea height
        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const res = await fetch(`${API}/ai-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    message: text,
                    session_id: activeSessionId,
                }),
                signal: controller.signal
            });

            if (!res.ok) {
                if (res.status === 401) { navigate('/'); return; }
                throw new Error('Failed');
            }

            // Streaming SSE response: read the body as a ReadableStream
            // and parse `data: {...}\n\n` frames one at a time. Tokens
            // are appended to a placeholder assistant message so the
            // answer types out live. The final `done` frame carries
            // session_id, sources, and the cleaned answer (with any
            // [SOURCES: …] marker stripped) — we replace the streamed
            // text with the cleaned answer at that point so the user
            // never sees the raw citation marker.
            setSending(false);
            const placeholderTs = new Date().toISOString();
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: '',
                sources: [],
                timestamp: placeholderTs,
                streaming: true,
            }]);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let streamedText = '';
            let finalized = false;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                // SSE frames are separated by `\n\n`; keep the last
                // (possibly-incomplete) frame in the buffer.
                let sepIdx;
                while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
                    const frame = buffer.slice(0, sepIdx);
                    buffer = buffer.slice(sepIdx + 2);
                    if (!frame.startsWith('data:')) continue;
                    const payload = frame.slice(5).trim();
                    if (!payload) continue;
                    let evt;
                    try { evt = JSON.parse(payload); } catch { continue; }
                    if (evt.type === 'token' && typeof evt.text === 'string') {
                        streamedText += evt.text;
                        // Hide the raw [SOURCES: …] marker from what
                        // the user sees mid-stream by regex-stripping
                        // it from the displayed text.
                        const visible = streamedText.replace(/\s*\[SOURCES?:[^\]]*\]?\s*$/, '');
                        setMessages(prev => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last && last.timestamp === placeholderTs) {
                                next[next.length - 1] = { ...last, content: visible };
                            }
                            return next;
                        });
                    } else if (evt.type === 'done') {
                        finalized = true;
                        if (evt.session_id) {
                            setActiveSessionId(evt.session_id);
                            if (searchParams.get('q')) {
                                navigate(`/search?session=${evt.session_id}`, { replace: true });
                            }
                        }
                        // Swap the streamed text for the cleaned
                        // answer + attach source cards + drop the
                        // streaming flag so the "typing…" affordance
                        // disappears.
                        setMessages(prev => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last && last.timestamp === placeholderTs) {
                                next[next.length - 1] = {
                                    ...last,
                                    content: evt.answer || last.content,
                                    sources: evt.sources || [],
                                    streaming: false,
                                };
                            }
                            return next;
                        });
                    }
                }
            }
            // Safety net: if the connection dropped without a `done`
            // frame, keep whatever was streamed so far and un-flag the
            // streaming state — user sees a partial answer, not a
            // stuck "typing…" spinner.
            if (!finalized) {
                setMessages(prev => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && last.timestamp === placeholderTs) {
                        next[next.length - 1] = { ...last, streaming: false };
                    }
                    return next;
                });
            }
            loadSessions();
        } catch (e) {
            if (e.name === 'AbortError') return;
            setSending(false);
            setIsTyping(false);
            toast.error('Something went wrong. Please try again.');
        } finally {
            abortControllerRef.current = null;
        }
    };

    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const deleteSession = async (sessionId) => {
        try {
            await fetch(`${API}/ai-chat/sessions/${sessionId}`, { method: 'DELETE', credentials: 'include' });
            setSessions(prev => prev.filter(s => s.session_id !== sessionId));
            if (activeSessionId === sessionId) startNewChat();
            setDeleteConfirmId(null);
        } catch (err) { console.error('Delete session error:', err); }
    };

    const deleteAllSessions = async () => {
        try {
            await fetch(`${API}/ai-chat/sessions`, { method: 'DELETE', credentials: 'include' });
            setSessions([]);
            startNewChat();
            setShowClearConfirm(false);
        } catch (err) { console.error('Delete all sessions error:', err); }
    };

    const formatTime = (ts) => {
        if (!ts) return '';
        try {
            return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch { return ''; }
    };

    const quickPrompts = [
        { icon: Search, text: 'What did I save about travel?' },
        { icon: BookOpen, text: 'Summarize my recent bookmarks' },
        { icon: Lightbulb, text: 'Key insights from my saved content' },
        { icon: Sparkles, text: 'What recipes did I bookmark?' },
    ];

    // Auto-grow textarea
    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (authLoading) return null;

    return (
        <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#fafaf8', overscrollBehavior: 'none' }} data-testid="ai-chat-page">
            <Toaster position="top-center" richColors />

            {/* Header - minimal */}
            <header className="border-b bg-white/95 backdrop-blur-md px-4 py-2.5 flex items-center gap-3 shrink-0 z-10"
                    style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                        <BrainCircuit className="w-4 h-4 text-white" />
                    </div>
                    <h1 className="font-semibold text-sm" style={{ color: '#1a1a1a' }}>Ask BrainSync</h1>
                </div>
                <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0 sm:hidden" onClick={() => setShowSidebar(!showSidebar)} data-testid="toggle-sessions">
                    <MessageSquare className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0" onClick={startNewChat} data-testid="new-chat-btn">
                    <Plus className="w-4 h-4" />
                </Button>
            </header>

            <div className="flex flex-1 overflow-hidden relative">
                {/* Sessions sidebar - permanent left rail on desktop
                    (ChatGPT-style, always visible so users can hop
                    between conversations without opening a drawer),
                    slide-in drawer on mobile (toggled via the
                    MessageSquare button in the header). The mobile
                    overlay backdrop only renders when the drawer is
                    open. */}
                {showSidebar && (
                    <div className="fixed inset-0 bg-black/20 z-20 sm:hidden" onClick={() => setShowSidebar(false)} />
                )}
                <div
                    className={`${showSidebar ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 w-72 z-30 bg-white border-r flex flex-col transition-transform duration-200 sm:relative sm:inset-auto sm:z-auto sm:translate-x-0 sm:w-64 sm:shrink-0`}
                    style={{ borderColor: 'rgba(0,0,0,0.06)' }}
                    data-testid="sessions-sidebar"
                >
                    <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        <span className="text-sm font-semibold" style={{ color: '#1a1a1a' }}>History</span>
                        <div className="flex items-center gap-1">
                            {sessions.length > 0 && (
                                <Button variant="ghost" size="sm" className="text-xs h-7 px-2 rounded-lg"
                                        style={{ color: '#ef4444', fontSize: '11px' }}
                                        onClick={() => setShowClearConfirm(true)}
                                        data-testid="delete-all-chats">
                                    <Trash2 className="w-3 h-3 mr-1" /> Clear
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" className="sm:hidden rounded-full w-7 h-7 p-0" onClick={() => setShowSidebar(false)}>
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    {showClearConfirm && (
                        <div className="mx-2 mt-2 p-3 rounded-lg" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                            <p className="text-xs font-medium mb-2" style={{ color: '#991b1b' }}>Delete all conversations?</p>
                            <div className="flex gap-2">
                                <button onClick={deleteAllSessions} className="flex-1 text-xs font-medium py-1.5 rounded-lg text-white" style={{ background: '#ef4444' }}
                                        data-testid="confirm-delete-all">Delete all</button>
                                <button onClick={() => setShowClearConfirm(false)} className="flex-1 text-xs font-medium py-1.5 rounded-lg" style={{ background: '#f3f4f6', color: '#555' }}
                                        data-testid="cancel-delete-all">Cancel</button>
                            </div>
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                        {sessions.map(s => (
                            <div key={s.session_id}>
                                <div
                                    onClick={() => {
                                        loadSession(s.session_id);
                                        // On mobile, close the drawer after
                                        // picking a session so the user
                                        // sees the conversation. Desktop
                                        // rail stays open (permanent).
                                        if (window.matchMedia('(max-width: 639px)').matches) setShowSidebar(false);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors text-sm active:bg-secondary/80"
                                    style={{
                                        background: activeSessionId === s.session_id ? 'rgba(16,185,129,0.08)' : 'transparent',
                                        color: activeSessionId === s.session_id ? '#059669' : '#555'
                                    }}
                                    data-testid={`session-${s.session_id}`}
                                >
                                    <MessageSquare className="w-3.5 h-3.5 shrink-0 opacity-50" />
                                    <span className="flex-1 truncate text-[13px]">{s.title}</span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(s.session_id); }}
                                        className="p-1.5 rounded-lg hover:bg-red-50 transition-all shrink-0"
                                        style={{ color: '#d4d4d4' }}
                                        onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                        onMouseLeave={e => e.currentTarget.style.color = '#d4d4d4'}
                                        data-testid={`delete-session-${s.session_id}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                {deleteConfirmId === s.session_id && (
                                    <div className="mx-2 mb-1 p-2.5 rounded-lg animate-in fade-in-0 slide-in-from-top-1 duration-150" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                                        <p className="text-xs font-medium mb-2" style={{ color: '#991b1b' }}>Delete this conversation?</p>
                                        <div className="flex gap-2">
                                            <button onClick={() => deleteSession(s.session_id)} className="flex-1 text-xs font-medium py-1.5 rounded-lg text-white" style={{ background: '#ef4444' }}
                                                    data-testid={`confirm-delete-${s.session_id}`}>Delete</button>
                                            <button onClick={() => setDeleteConfirmId(null)} className="flex-1 text-xs font-medium py-1.5 rounded-lg" style={{ background: '#f3f4f6', color: '#555' }}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {sessions.length === 0 && (
                            <div className="text-center py-16 px-4">
                                <MessageSquare className="w-8 h-8 mx-auto mb-3" style={{ color: '#ddd' }} />
                                <p className="text-xs" style={{ color: '#aaa' }}>No conversations yet</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Chat area */}
                <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-6" style={{ overscrollBehavior: 'none' }} data-testid="chat-messages">
                        <div className="max-w-2xl mx-auto space-y-5">
                            {/* Empty state - clean centered greeting */}
                            {messages.length === 0 && !isTyping && (
                                <div className="flex flex-col items-center justify-center min-h-[55vh] text-center px-4">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                                         style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 8px 24px rgba(16,185,129,0.25)' }}>
                                        <BrainCircuit className="w-7 h-7 text-white" />
                                    </div>
                                    <h2 className="text-lg font-semibold mb-1.5" style={{ color: '#1a1a1a' }}>What would you like to know?</h2>
                                    <p className="text-sm max-w-xs" style={{ color: '#999' }}>
                                        Search across your saved bookmarks with AI
                                    </p>
                                </div>
                            )}

                            {/* Messages */}
                            {messages.map((msg, i) => (
                                <div key={`${msg.role}-${i}-${msg.timestamp || i}`}
                                     className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in-0 slide-in-from-bottom-2 duration-300`}
                                     style={{ animationDelay: `${Math.min(i * 50, 200)}ms` }}>
                                    <div className={`max-w-[88%] sm:max-w-[75%]`}>
                                        <div
                                            className={`rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'rounded-br-md' : 'rounded-bl-md'}`}
                                            style={msg.role === 'user' 
                                                ? { background: '#2E3A2F', color: 'white' }
                                                : { background: 'white', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }
                                            }
                                            dir={isHebrewText(msg.content) ? 'rtl' : 'ltr'}
                                        >
                                            {msg.role === 'assistant' ? (
                                                <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1 prose-li:my-0.5 prose-headings:text-sm"
                                                     style={{ color: '#333', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                                    {/* Blinking caret while tokens are still streaming
                                                        in. Kept as a single narrow bar so it doesn't
                                                        push content around; disappears the moment the
                                                        `done` frame flips `streaming` to false. */}
                                                    {msg.streaming && (
                                                        <span
                                                            className="inline-block w-0.5 h-4 ml-0.5 align-text-bottom rounded-full animate-pulse"
                                                            style={{ background: '#10b981' }}
                                                            data-testid="streaming-cursor"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                                            )}
                                        </div>

                                        {/* Sources - real cards once loaded, shimmer skeleton while
                                            the answer is still streaming so the space is reserved and
                                            the final source strip doesn't cause a jarring layout shift
                                            when it slides into place. */}
                                        {msg.sources && msg.sources.length > 0 ? (
                                            <div className="mt-2.5 space-y-1.5">
                                                <div className="flex items-center gap-1.5 ml-1">
                                                    <BookOpen className="w-3 h-3" style={{ color: '#10b981' }} />
                                                    <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#10b981' }}>
                                                        Sources ({msg.sources.length})
                                                    </span>
                                                </div>
                                                <div className="space-y-1.5">
                                                    {msg.sources.map((src, j) => (
                                                        <SourceCard key={j} source={src} navigate={navigate} />
                                                    ))}
                                                </div>
                                            </div>
                                        ) : msg.streaming ? (
                                            <div className="mt-2.5 space-y-1.5" data-testid="sources-skeleton" aria-hidden="true">
                                                <div className="flex items-center gap-1.5 ml-1">
                                                    <BookOpen className="w-3 h-3 opacity-40" style={{ color: '#10b981' }} />
                                                    <span className="text-[10px] font-medium uppercase tracking-wider opacity-40" style={{ color: '#10b981' }}>
                                                        Sources
                                                    </span>
                                                </div>
                                                <div className="space-y-1.5">
                                                    {[0, 1].map(k => (
                                                        <div key={k} className="rounded-lg h-9 animate-pulse" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.10)' }} />
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}

                                        <p className="text-[10px] mt-1 ml-1" style={{ color: '#d4d4d4' }}>
                                            {formatTime(msg.timestamp)}
                                        </p>
                                    </div>
                                </div>
                            ))}

                            {/* Typing indicator */}
                            {(sending || isTyping) && (
                                <div className="flex justify-start animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
                                    <div className="max-w-[88%] sm:max-w-[75%]">
                                        <div className="rounded-2xl rounded-bl-md px-4 py-3"
                                             style={{ background: 'white', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                            {isTyping && typingText ? (
                                                <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1.5"
                                                     style={{ color: '#333', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                    <ReactMarkdown>{typingText}</ReactMarkdown>
                                                    <span className="inline-block w-0.5 h-4 animate-pulse ml-0.5 align-text-bottom rounded-full" style={{ background: '#10b981' }} />
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2.5 py-0.5">
                                                    <div className="flex gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#10b981', animationDelay: '0ms' }} />
                                                        <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#10b981', animationDelay: '150ms' }} />
                                                        <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#10b981', animationDelay: '300ms' }} />
                                                    </div>
                                                    <span className="text-xs" style={{ color: '#999' }}>Searching your bookmarks...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    {/* First-run primer chips. Shown only when the chat
                        is empty so returning users aren't distracted.
                        Clicking a chip fires the query immediately - the
                        original two-step (populate → press send) was
                        friction for an "onboarding taste" affordance. */}
                    {messages.length === 0 && !isTyping && (
                        <div className="shrink-0 px-4 pb-2">
                            <div className="max-w-2xl mx-auto flex flex-wrap gap-2 justify-center">
                                {quickPrompts.map(({ icon: Icon, text }) => (
                                    <button
                                        key={text}
                                        onClick={() => sendMessage(text)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                                        style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', color: '#555' }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.color = '#059669'; e.currentTarget.style.background = 'rgba(16,185,129,0.04)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; e.currentTarget.style.color = '#555'; e.currentTarget.style.background = 'white'; }}
                                        data-testid="prompt-chip"
                                    >
                                        <Icon className="w-3 h-3" />
                                        {text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input bar - ChatGPT-style with textarea */}
                    <div className="shrink-0 px-3 py-2.5 sm:py-3" style={{ background: '#fafaf8' }}>
                        <div className="max-w-2xl mx-auto">
                            <div className="flex items-end gap-2 rounded-2xl px-3 py-2 transition-all"
                                 style={{ background: 'white', border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                                <textarea
                                    ref={textareaRef}
                                    value={input}
                                    onChange={handleTextareaChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Ask about your bookmarks..."
                                    rows={1}
                                    className="flex-1 min-w-0 resize-none text-sm leading-relaxed py-1.5 focus:outline-none bg-transparent"
                                    style={{ color: '#1a1a1a', maxHeight: '120px' }}
                                    disabled={sending}
                                    data-testid="chat-input"
                                />
                                <div className="flex items-center gap-1 shrink-0 pb-0.5">
                                    <VoiceButton
                                        onResult={(text) => setInput(text)}
                                        onInterim={(text) => setInput(text)}
                                        autoSubmit={false}
                                        disabled={sending || isTyping}
                                        className="w-8 h-8 rounded-lg shrink-0"
                                        data-testid="voice-chat-btn"
                                    />
                                    {(sending || isTyping) ? (
                                        <Button
                                            type="button"
                                            onClick={stopGenerating}
                                            className="rounded-lg h-8 w-8 p-0 shrink-0"
                                            style={{ background: '#ef4444', color: 'white' }}
                                            data-testid="stop-btn"
                                        >
                                            <Square className="w-3.5 h-3.5" fill="white" />
                                        </Button>
                                    ) : (
                                        <Button
                                            type="button"
                                            onClick={sendMessage}
                                            disabled={!input.trim()}
                                            className="rounded-lg h-8 w-8 p-0 shrink-0 transition-all"
                                            style={{ 
                                                background: input.trim() ? '#10b981' : '#e5e5e5', 
                                                color: 'white',
                                            }}
                                            data-testid="send-btn"
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIChatPage;
